import { Component, Injector, input, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { INavData, SidebarModule } from '@coreui/angular';
import { TranslateService } from '@ngx-translate/core';
import { LocalStorageService } from 'ngx-localstorage';
import { Subscription } from 'rxjs';
import { environment } from '../../environments/environment';
import { navItems } from '../_nav';
import { appCommonConstants } from '../app.constants';
import { AppInfoComponent } from '../modules/@shared/components/app-info/app-info.component';
import { AppUiInfoComponent } from '../modules/@shared/components/app-ui-info/app-ui-info.component';
import { AppProfilePasswordChangeComponent } from '../modules/profile/components/password-change/password-change.component';
import { AppTraceSystemLogMessageCounterComponent } from '../modules/trace/components/trace-systemlogmessage/trace-systemlogmessage-counter/trace-systemlogmessage-counter.component';
import { AppAuthService } from '../services/auth.service';
import { AppAuthDataService } from '../services/authData.service';
import { AppDialogService } from '../services/dialog.service';
import { AppEventService } from '../services/event.service';
import { AppLocaleService } from '../services/locale.service';

export interface IContainerAlertMessage {
  dismissible: boolean;
  message: string;
  type: "danger" | "info" | "success" | "warning"
}

interface IUserInfo {
  asNoFilter: boolean;
  avatar: string;
  azureLogin: boolean;
  fullname: string;
  isImpersonated?: boolean;
  lastLogin?: Date;
  username: string;
}

/** Classe che definisce proprietà e metodi comuni per container che espongano "servizi" di default */
@Component({
  selector: 'app-container-default',
  template: '',
  standalone: true,
  imports: [
    SidebarModule
  ]
})

export class AppContainerDefaultClass implements OnDestroy, OnInit {
  constructor(public appDialogService: AppDialogService,
    public authDataService: AppAuthDataService,
    public authService: AppAuthService,
    public eventService: AppEventService,
    public injector: Injector,
    public translate: TranslateService,
  ) {
    let localstorage = injector.get(LocalStorageService);

    this.environment = <string>localstorage.get(appCommonConstants.global.environmentDescription);
    this.environmentColor = <string>localstorage.get(appCommonConstants.global.environmentColor);

    this.changes = new MutationObserver((mutations) => {
      this.sidebarMinimized = document.body.classList.contains('sidebar-minimized');
    });

    this.changes.observe((this.element as Element), {
      attributes: true
    });

    setTimeout(() => {
      // Impostazione menu
      let doIt = (): void => {
        this.getUserData();
        this.navItems = this.getNavItems();
      };
      // Menu translate: in ascolto sull'evento di cambio lingua
      this.subscriptions.push(
        this.eventService.on(appCommonConstants.events.languageSelection).subscribe((language) => {
          language = language as string;
          doIt();
        })
      );
    }, 100);
  }

  getUserData() {
    let authData = this.authDataService.authDataGet();

    this.user = {
      asNoFilter: authData?.settings?.asNoFilter ?? false,
      avatar: '', // TODO
      azureLogin: this.authDataService.getLoginData().azureLogin ?? false,
      fullname: authData?.fullname ?? '',
      isImpersonated: authData?.settings?.isImpersonated || false,
      lastLogin: authData?.lastLogin,
      username: authData?.userName ?? ''
    };
  }

  ngOnDestroy() {
    // Deregistrazione eventi
    this.subscriptions?.forEach(x => x?.unsubscribe());
    clearInterval(this.intervalSub);
  }

  ngOnInit() {
    this.subscriptions.push(this.eventService.on(appCommonConstants.events.containerAlert).subscribe((x: any) => {
      this.alert = x;
    }));

    this.intervalSub = setInterval(() => this.authService.checkLoggedIn().subscribe(), this.interval);

    // Initialize navigation
    this.getUserData();
    this.navItems = this.getNavItems();

    this.navItems = this.navItems.map((x, index) => {
      if (x.children && x.children.length > 0) {
        for (let i = 0; i < x.children.length; i++) {
          x.children[i].class = i === 0 ? 'nav-item-first-level' : 'nav-sub-level';
        }
      }
      return x;
    });

    /** Toggle della visibilità della pagina in full-screen mode. Ref: https://stackoverflow.com/a/23913497 */
    // setTimeout necessario per evitare che l'evento venga eseguito prima del caricamento della pagina
    setTimeout(() => {
      // Resize dinamico dell'altezza del main in base all'altezza di header e footer (se presenti)
      let offset: number = ((document.querySelector('c-header') as HTMLElement)?.offsetHeight ?? 0) + ((document.querySelector('c-footer') as HTMLElement)?.offsetHeight ?? 0);
      let mainElement = document.querySelector('main') as HTMLElement;
      if (mainElement) {
        mainElement.style.height = `calc(100vh - ${(offset > 0 ? offset : 114)}px)`; // 114px = altezza header + footer di default
      }

      document.querySelector('.full-screen-toggle')?.addEventListener("click", ($event) => {
        if (($event?.srcElement as HTMLElement).classList.contains("fa-compress")) {
          this.fullScreenClose();
        } else {
          this.fullScreenOpen();
        }
      });
    });
  }

  onItemClick(event: any) {
    // set dropdown open
  }

  @Input() public alert?: IContainerAlertMessage;
  @Input() public interval = 60000;
  @ViewChild("systemlogmessage") systemlogmessage?: AppTraceSystemLogMessageCounterComponent;

  /** Stringa contenente il nome dell'applicazione */
  public applicationName: string = environment.appSettings.applicationName;
  /** Stringa contenente la versione dell'applicazione */
  public appVersion: string = environment.appSettings.appVersion;
  public element: HTMLElement = document.body;

  /** Voci del menu */
  public navItems: Array<INavData> = [];
  public policy = environment.policy;
  public showEnvironment = environment.appSettings.showEnvironment;
  public showNotify = false;
  public sidebarMinimized = true;

  public profileEditUrl: string = appCommonConstants.defaultRoutes.profileEdit;
  public sideBarVisible: boolean = true;
  public systemMessageUrl: string = appCommonConstants.defaultRoutes.systemMessage;
  public useLanguageSelector = environment.appSettings.useLanguageSelector;

  // Utente loggato
  public user: IUserInfo = {} as IUserInfo;
  // Anno attuale
  public year: number = (new Date()).getFullYear();

  public environment?: string;
  public environmentColor?: string;
  public sidebarId = input('sidebar');

  protected intervalSub: any;

  public get homePage(): string {
    return this.authDataService.getHomePage();
  }

  public get isFullScreen(): boolean {
    return window.innerHeight === screen.height;
  }

  public get userTitle(): string {
    let subtitle = "";

    if (this.user.asNoFilter) {
      subtitle += (this.translate.instant("Login amministrativo") as string).toLowerCase();
    }

    if (this.user.isImpersonated) {
      subtitle += (subtitle.length ? " | " : "") + this.translate.instant("impersonated");
    }

    if (subtitle.length) {
      subtitle = ` (${subtitle})`;
    }

    if (this.user.fullname != null && this.user.username !== this.user.fullname) {
      return `${this.user.username?.toUpperCase()} (${this.user.fullname})${subtitle}`;
    } else {
      return `${this.user.username?.toUpperCase()}${subtitle}`;
    }
  }

  protected subscriptions: Array<Subscription> = [];

  private changes: MutationObserver;

  /**
   * Apre la finestra con la lista delle librerie installate nel backend
   */
  public appInfo(): void {
    this.appDialogService.dialogOpen({
      actions: [
        { text: this.translate.instant('WORD.CHIUDI'), themeColor: "primary" }
      ],
      content: AppInfoComponent,
      height: 500,
      isTitleHtml: true,
      minWidth: 400,
      title: `<span><i class="fas fa-info"></i>&nbsp;${this.translate.instant('WORD.APPINFO')}</span>`
    });
  }

  /** Apre la finestra di modifica password */
  public changePassword(): void {
    if (this.user.azureLogin) {
      return;
    }
    this.appDialogService.dialogOpen({
      content: AppProfilePasswordChangeComponent,
      minHeight: "35%",
      minWidth: "70%"
    });
  }

  /** Ritorna alla home page dell'applicazione  */
  public gotoHomePage(): void {
    this.authService.gotoHome();
  }

  /** Effettua il logout dall'applicazione */
  public logout(): void {
    this.authService.logOut();
  }

  /**
   * Evento scatenato alla chiusura dell'alert
   * @param $event
   */
  public onAlertClosed($event: any): void {
    this.alert = undefined;
  }

  /** Toggle della finestra di notifiche */
  public showNotifyToggle(): void {
    this.showNotify = !this.showNotify;
  }

  public showTraceLogMessage(): void {
    this.systemlogmessage?.showMessage();
  }

  /**
   * Apre la finestra con informazioni sulla UI
   */
  public uiInfo(): void {
    this.appDialogService.dialogOpen({
      actions: [
        { text: this.translate.instant('WORD.CHIUDI'), themeColor: "primary" }
      ],
      content: AppUiInfoComponent,
      isTitleHtml: true,
      minHeight: 350,
      title: `<span><i class="fas fa-info-circle"></i>&nbsp;${this.translate.instant('WORD.APPINFO')}</span>`,
      width: 450
    });
  }

  /**
   * Chiusura visualizzazione finestra browser full-screen
   */
  private fullScreenClose(): void {
    // Get the documentElement (<html>) to display the page in fullscreen
    let elem: any = document;
    if (elem.exitFullscreen) {
      elem.exitFullscreen();
    } else if (elem.mozCancelFullScreen) {  // Firefox
      elem.mozCancelFullScreen();
    } else if (elem.webkitExitFullscreen) { // Chrome, Safari and Opera
      elem.webkitExitFullscreen();
    } else if (elem.msExitFullscreen) {     // IE/Edge
      elem.msExitFullscreen();
    }
  }

  /**
   * Visualizzazione finestra browser full-screen
   */
  private fullScreenOpen(): void {
    // Get the document
    let elem: any = document.documentElement;

    if (elem.requestFullscreen) {
      elem.requestFullscreen();
    } else if (elem.mozRequestFullScreen) {     // Firefox
      elem.mozRequestFullScreen();
    } else if (elem.webkitRequestFullscreen) {  // Chrome, Safari and Opera
      elem.webkitRequestFullscreen();
    } else if (elem.msRequestFullscreen) {      // IE/Edge
      elem.msRequestFullscreen();
    }
  }

  /**
   * Eseue la traduzione delle voci di menu
   * @param nav
   * @param resxPrefix
   */
  private getNavItems(): INavData[] {
    let doIt = (nav: Array<INavData>) => {
      let menu = Object.assign([], nav);
      let appLocaleService = this.injector.get(AppLocaleService);

      let translateChildren = (elementList: Array<INavData>) => {
        for (let i = 0; i < elementList.length; i++) {
          let element: INavData = elementList[i];

          element.name = appLocaleService.getMenuTranslate(element?.name ?? "") ?? element.name;

          // ...ci sono figli?
          if (element.children != null && element.children.length > 0) {
            translateChildren(element.children);
          }
        };
      }

      translateChildren(menu);

      return menu;
    }

    if (environment.appSettings.useStaticMenu) {
      // Static menu
      return doIt(navItems);
    } else {
      // Dynamic menu
      return doIt(this.authDataService.authDataGet().nav);
    }
  }
}
